import { z } from "zod";

/**
 * Shared DTO — Admin bật/tắt usecase AI-write (US-AIF.P0-0243 · @UC9415 · @gate config,ai).
 *
 * Một nguồn sự thật cho api (controller + DTO validate biên) và admin web (danh mục năng lực).
 *
 * BẤT BIẾN:
 * - Usecase KHÔNG tạo/xoá được qua API — chỉ OVERRIDE bật/tắt usecase có sẵn trong AI_CAPABILITY_CATALOG
 *   (domain). usecaseCode lạ ⇒ 404.
 * - Kế thừa: default catalog (enabledByDefault) ⊕ override system ⊕ override group (group thắng system
 *   thắng default). Xoá override = xoá row (kế thừa lại) — PATCH `{ enabled: null }` ⇒ XOÁ override.
 * - `.strict()` — chặn key lạ tại biên (C5 fail-closed input).
 * - PATCH chạm QUYỀN AI GHI TIỀN ⇒ RBAC SUPER_ADMIN/OPS (server @Roles) + MỌI PATCH ghi 1 AuditLog.
 */

/** Scope override: 'system' (toàn hệ thống) | 'group' (một nhóm — đè system). */
export const aiCapabilityScopeSchema = z.enum(["system", "group"]);
export type AiCapabilityScope = z.infer<typeof aiCapabilityScopeSchema>;

/** Override bật/tắt đã lưu ở một scope. */
export const aiCapabilityOverrideSchema = z
  .object({
    scope: aiCapabilityScopeSchema,
    groupId: z.string().uuid().nullable(),
    enabled: z.boolean(),
    updatedBy: z.string(),
    updatedAt: z.string(),
  })
  .strict();
export type AiCapabilityOverride = z.infer<typeof aiCapabilityOverrideSchema>;

/** Một usecase trong danh mục admin: metadata catalog + trạng thái HIỆU LỰC + override đã lưu. */
export const adminAiCapabilityItemSchema = z
  .object({
    usecaseCode: z.string(),
    labelVi: z.string(),
    labelEn: z.string(),
    tier: z.literal("write"),
    /** Cửa GUI thay thế khi usecase tắt (deep-link — KHÔNG ngõ cụt). */
    deepLink: z.string(),
    /** Mặc định BẤT BIẾN từ catalog (code) — admin thấy mình đang lệch khỏi cái gì. */
    defaultEnabled: z.boolean(),
    /** Trạng thái HIỆU LỰC sau kế thừa (default ⊕ system ⊕ group). */
    effectiveEnabled: z.boolean(),
    /** Override đã lưu ở từng scope (null = chưa override). */
    systemOverride: aiCapabilityOverrideSchema.nullable(),
    groupOverride: aiCapabilityOverrideSchema.nullable(),
  })
  .strict();
export type AdminAiCapabilityItem = z.infer<typeof adminAiCapabilityItemSchema>;

/** Query GET /v1/admin/ai/capabilities — `groupId` tuỳ chọn: có ⇒ tính hiệu lực cho NHÓM đó. */
export const adminAiCapabilitiesQuerySchema = z
  .object({
    groupId: z.string().uuid().optional(),
  })
  .strict();
export type AdminAiCapabilitiesQuery = z.infer<typeof adminAiCapabilitiesQuerySchema>;

/** Response GET /v1/admin/ai/capabilities. */
export const adminAiCapabilitiesResponseSchema = z
  .object({
    groupId: z.string().nullable(),
    items: z.array(adminAiCapabilityItemSchema),
  })
  .strict();
export type AdminAiCapabilitiesResponse = z.infer<typeof adminAiCapabilitiesResponseSchema>;

/**
 * Body PATCH /v1/admin/ai/capabilities/:usecaseCode — bật/tắt usecase.
 *
 * `scope='group'` BẮT BUỘC kèm `groupId` (refine) — không "override nhóm mồ côi". `enabled` boolean =
 * đặt bật/tắt; `enabled: null` = XOÁ override (kế thừa lại). Không có `enabled` field ⇒ 400 (gọi nhầm).
 */
export const patchAdminAiCapabilitySchema = z
  .object({
    scope: aiCapabilityScopeSchema,
    groupId: z.string().uuid().optional(),
    enabled: z.boolean().nullable(),
  })
  .strict()
  .refine((b) => (b.scope === "group" ? typeof b.groupId === "string" : b.groupId === undefined), {
    message: "scope='group' cần groupId; scope='system' KHÔNG được kèm groupId",
    path: ["groupId"],
  });
export type PatchAdminAiCapabilityRequest = z.infer<typeof patchAdminAiCapabilitySchema>;
